import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

// Define the shape of company onboarding data
export interface OnboardingData {
        // Company Information
        companyName: string;
        businessType: string;
        companyNumber: string;
        vatNumber: string;
        postcode: string;

        // Financial Information
        financialYearEnd: string;
        vatScheme: string;

        // Industry & Size
        industry: string;
        employeeCount: string;
        annualRevenue: string;

        // Banking
        bankConnected: boolean;

        // Preferences
        currency: string;
        timezone: string;
}

export interface OnboardingContextType {
        isOnboardingComplete: boolean;
        isLoading: boolean;
        onboardingData: OnboardingData;
        completionPercentage: number;
        requiredFields: string[];
        filledFields: string[];
        updateOnboardingData: (data: Partial<OnboardingData>) => void;
        completeOnboarding: () => void;
        resetOnboarding: () => void;
        getCompanyContext: () => string;
}

const defaultOnboardingData: OnboardingData = {
        companyName: '',
        businessType: '',
        companyNumber: '',
        vatNumber: '',
        postcode: '',
        financialYearEnd: '',
        vatScheme: '',
        industry: '',
        employeeCount: '',
        annualRevenue: '',
        bankConnected: false,
        currency: 'USD',
        timezone: 'Europe/London',
};

// Fields that count towards completion percentage
const REQUIRED_FIELDS: (keyof OnboardingData)[] = [
        'companyName',
        'businessType',
        'postcode',
        'industry',
        'employeeCount',
];

const OPTIONAL_FIELDS: (keyof OnboardingData)[] = [
        'companyNumber',
        'vatNumber',
        'financialYearEnd',
        'vatScheme',
        'annualRevenue',
];

const ALL_TRACKED_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

const STORAGE_KEY = 'onboarding_data';
const COMPLETE_KEY = 'onboarding_complete';

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
        const [onboardingData, setOnboardingData] = useState<OnboardingData>(defaultOnboardingData);
        // Initialize synchronously from localStorage to prevent redirect flash
        const [isOnboardingComplete, setIsOnboardingComplete] = useState(() => {
                return localStorage.getItem(COMPLETE_KEY) === 'true';
        });
        const [isLoading, setIsLoading] = useState(true);

        // Load data from localStorage on mount
        useEffect(() => {
                const savedData = localStorage.getItem(STORAGE_KEY);
                const savedComplete = localStorage.getItem(COMPLETE_KEY);

                if (savedData) {
                        try {
                                setOnboardingData(JSON.parse(savedData));
                        } catch (e) {
                                console.error('Failed to parse onboarding data:', e);
                        }
                }

                if (savedComplete === 'true') {
                        setIsOnboardingComplete(true);
                }

                setIsLoading(false);
        }, []);

        // Calculate completion percentage
        const calculateCompletion = useCallback(() => {
                let filled = 0;
                const filledFieldsList: string[] = [];

                ALL_TRACKED_FIELDS.forEach((field) => {
                        const value = onboardingData[field];
                        // Check if field has a meaningful value (non-empty string or true for booleans)
                        const isFilled = typeof value === 'boolean' ? value : (value !== '' && value !== undefined);
                        if (isFilled) {
                                filled++;
                                filledFieldsList.push(field);
                        }
                });

                return {
                        percentage: Math.round((filled / ALL_TRACKED_FIELDS.length) * 100),
                        filledFields: filledFieldsList,
                };
        }, [onboardingData]);

        const { percentage: completionPercentage, filledFields } = calculateCompletion();

        const updateOnboardingData = useCallback((data: Partial<OnboardingData>) => {
                setOnboardingData((prev) => {
                        const updated = { ...prev, ...data };
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                        return updated;
                });
        }, []);

        const completeOnboarding = useCallback(() => {
                setIsOnboardingComplete(true);
                localStorage.setItem(COMPLETE_KEY, 'true');
                localStorage.setItem(STORAGE_KEY, JSON.stringify(onboardingData));
        }, [onboardingData]);

        const resetOnboarding = useCallback(() => {
                setOnboardingData(defaultOnboardingData);
                setIsOnboardingComplete(false);
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem(COMPLETE_KEY);
        }, []);

        // Generate company context for AI
        const getCompanyContext = useCallback((): string => {
                const data = onboardingData;
                const parts: string[] = [];

                if (data.companyName) {
                        parts.push(`Company: ${data.companyName}`);
                }
                if (data.businessType) {
                        const typeLabels: Record<string, string> = {
                                'sole_trader': 'Sole Trader',
                                'partnership': 'Partnership',
                                'limited_company': 'Limited Company',
                                'llp': 'Limited Liability Partnership',
                                'charity': 'Charity',
                        };
                        parts.push(`Type: ${typeLabels[data.businessType] || data.businessType}`);
                }
                if (data.industry) {
                        parts.push(`Industry: ${data.industry}`);
                }
                if (data.employeeCount) {
                        parts.push(`Employees: ${data.employeeCount}`);
                }
                if (data.annualRevenue) {
                        parts.push(`Annual Revenue: ${data.annualRevenue}`);
                }
                if (data.vatNumber) {
                        parts.push(`VAT Registered: Yes (${data.vatNumber})`);
                }
                if (data.companyNumber) {
                        parts.push(`Secretary of State: ${data.companyNumber}`);
                }
                if (data.postcode) {
                        parts.push(`Location: ${data.postcode}`);
                }

                if (parts.length === 0) {
                        return 'No company information available. User has not completed onboarding.';
                }

                return `User Company Context:\n${parts.join('\n')}`;
        }, [onboardingData]);

        const value: OnboardingContextType = {
                isOnboardingComplete,
                isLoading,
                onboardingData,
                completionPercentage,
                requiredFields: REQUIRED_FIELDS,
                filledFields,
                updateOnboardingData,
                completeOnboarding,
                resetOnboarding,
                getCompanyContext,
        };

        return (
                <OnboardingContext.Provider value={value}>
                        {children}
                </OnboardingContext.Provider>
        );
};

export const useOnboarding = (): OnboardingContextType => {
        const context = useContext(OnboardingContext);
        if (context === undefined) {
                throw new Error('useOnboarding must be used within an OnboardingProvider');
        }
        return context;
};

export default OnboardingContext;
